import { WS_GRID_DEFAULT_PRESET_ID } from '@/domain/documents/constants';
import { layoutFromPresetConfig, withMissingColumns } from '@/domain/documents/gridPreset/convert';
import type { GridPresetResolvedPayload } from '@/domain/documents/gridPreset/gridPresetActions';
import type { GridLayout, StoredGridPreset } from '@/domain/documents/gridPreset/schemas';
import type { SavedColumnLayout } from '@/domain/documents/savedColumnLayout';
import type { DocumentsGridPreset } from '@/domain/documents/schemas';

// Which layout the workspace grid starts with, in order:
// 1. a shared preset: from a link — as saved (the caller passes no stored choice); or the stored shared preset after a
//    reload — with the stored changes;
// 2. the stored preset, if it is still in the list (Default always is), with the stored changes;
// 3. Default.
// Each stored part replaces that part of the fresh saved version; the other parts are the server's, so an edit saved
// elsewhere shows. A part changed both here and there keeps the change made here.
// The columns get the column definitions' columns they do not list (`withMissingColumns`).
export const resolveGridPreset = ({
  companyId,
  presets,
  shared,
  defaultColumns,
  stored,
}: {
  companyId: string;
  presets: ReadonlyArray<DocumentsGridPreset>;
  shared: DocumentsGridPreset | null;
  defaultColumns: SavedColumnLayout;
  stored: StoredGridPreset | null;
}): GridPresetResolvedPayload => {
  const defaultLayout: GridLayout = { filters: {}, sorting: [], columns: [...defaultColumns] };
  const base = { companyId, defaultColumns: [...defaultColumns] };

  const withStored = (saved: GridLayout): GridLayout => {
    const current: GridLayout = { ...saved, ...stored?.changes };
    return { ...current, columns: withMissingColumns(current.columns, defaultColumns) };
  };

  if (shared) {
    const saved = layoutFromPresetConfig(shared.config, defaultColumns);
    const current = stored?.presetId === shared.id ? withStored(saved) : saved;
    return { ...base, presetId: shared.id, sharedPresetId: shared.id, saved, current };
  }

  const storedId = stored?.presetId ?? WS_GRID_DEFAULT_PRESET_ID;
  const storedPreset = presets.find(({ id }) => id === storedId);
  const saved =
    storedId === WS_GRID_DEFAULT_PRESET_ID
      ? defaultLayout
      : storedPreset && layoutFromPresetConfig(storedPreset.config, defaultColumns);

  if (!saved) {
    return {
      ...base,
      presetId: WS_GRID_DEFAULT_PRESET_ID,
      sharedPresetId: null,
      saved: defaultLayout,
      current: defaultLayout,
    };
  }

  return {
    ...base,
    presetId: storedId,
    sharedPresetId: null,
    saved,
    current: withStored(saved),
  };
};
